import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CdkMenu, CdkMenuItem, CdkMenuTrigger } from '@angular/cdk/menu';
import { finalize, map } from 'rxjs';
import { downloadCsv } from '../../core/functions/csv-export';
import { AuthService } from '../../core/services/auth/auth.service';
import { Release } from '../../models/release';
import { TestPlan, TestPlanStatus } from '../../models/test-plan';
import { ReleaseService } from '../../services/release/release.service';
import { TestCaseService } from '../../services/test-case/test-case.service';
import { ReleaseViewStateService } from '../../services/view-state/release-view-state.service';
import { ToastService } from '../../ui';

type StatusFilter = 'All' | 'InProgress' | 'NotStarted' | 'Completed';

/**
 * Release Home — tester view (PBI 55095). Shows the active release summary and the
 * tester's plans split into "Assigned to me" and "Unassigned" (claimable) sections.
 * The flat "Everyone" manager view is a separate page (PBI 55096).
 */
@Component({
  selector: 'app-release-home',
  standalone: true,
  imports: [CommonModule, RouterLink, CdkMenuTrigger, CdkMenu, CdkMenuItem],
  templateUrl: './release-home.component.html',
  styleUrl: './release-home.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReleaseHomeComponent {
  private auth = inject(AuthService);
  private releaseService = inject(ReleaseService);
  private testCaseService = inject(TestCaseService);
  private viewState = inject(ReleaseViewStateService);
  private router = inject(Router);
  private toast = inject(ToastService);
  private route = inject(ActivatedRoute);

  readonly release = toSignal(
    this.route.data.pipe(map((d) => d['releaseData'] as Release | null)),
    {
      initialValue: null,
    },
  );

  readonly search = signal('');
  readonly statusFilter = signal<StatusFilter>('All');

  constructor() {
    const releaseId = this.release()?.releaseId;
    if (releaseId != null) this.search.set(this.viewState.getSearch(releaseId));
  }

  /** Local assignment overrides applied after a claim/unassign so the UI updates instantly. */
  private readonly overrides = signal<Record<number, Partial<TestPlan>>>({});

  /** Plan ids with a claim request in flight, so the row can show a spinner. */
  private readonly claimingIds = signal<Set<number>>(new Set());

  /** Plan ids whose test cases are being fetched before jumping into execution. */
  private readonly openingIds = signal<Set<number>>(new Set());

  readonly statusFilters: { key: StatusFilter; label: string }[] = [
    { key: 'All', label: 'All' },
    { key: 'InProgress', label: 'In progress' },
    { key: 'NotStarted', label: 'Not started' },
    { key: 'Completed', label: 'Complete' },
  ];

  readonly currentUserId = computed(() => this.auth.userSig()?.userId ?? null);

  /** Leads/Admins can switch to the manager "Everyone" view (PBI 55096). */
  readonly isManager = this.auth.isManagerSig;

  readonly myInitials = computed(() => {
    const u = this.auth.userSig();
    return ((u?.firstName?.[0] ?? '') + (u?.lastName?.[0] ?? '')).toUpperCase() || '?';
  });

  /** All plans across the release's suites, with local assignment overrides applied. */
  readonly plans = computed<TestPlan[]>(() => {
    const r = this.release();
    const base = r ? (r.suites ?? []).flatMap((s) => s.testPlanSummaries ?? []) : [];
    const ov = this.overrides();
    return base.map((p) => (ov[p.testPlanId] ? { ...p, ...ov[p.testPlanId] } : p));
  });

  readonly assignedToMe = computed(() =>
    this.applyFilters(this.plans().filter((p) => p.assignedUserId === this.currentUserId())),
  );

  readonly unassigned = computed(() =>
    this.applyFilters(this.plans().filter((p) => p.assignedUserId == null)),
  );

  readonly myViewCount = computed(
    () =>
      this.plans().filter(
        (p) => p.assignedUserId === this.currentUserId() || p.assignedUserId == null,
      ).length,
  );

  readonly everyoneCount = computed(() => this.plans().length);

  /** True when the search box or a non-"All" status chip is narrowing the list. */
  readonly hasActiveFilters = computed(
    () => this.search().trim() !== '' || this.statusFilter() !== 'All',
  );

  isClaiming(p: TestPlan): boolean {
    return this.claimingIds().has(p.testPlanId);
  }

  isOpening(p: TestPlan): boolean {
    return this.openingIds().has(p.testPlanId);
  }

  /** Retry from the failure state. The release is fed by a blocking route resolver,
      so the simplest reliable retry is a full reload that re-runs it. */
  reload(): void {
    window.location.reload();
  }

  resetFilters(): void {
    this.setSearch('');
    this.statusFilter.set('All');
  }

  /** Pins the manager view for the rest of the session, so a lead who switches back
      to Everyone is not returned here on the next release they open (Bug 56800). */
  chooseEveryoneView(): void {
    this.viewState.setViewOverride('everyone');
  }

  /** Search is persisted per release so switching to the Everyone view keeps the
      term instead of silently resetting it (Bug 56799). */
  setSearch(term: string): void {
    this.search.set(term);
    const releaseId = this.release()?.releaseId;
    if (releaseId != null) this.viewState.setSearch(releaseId, term);
  }

  // ---- release summary ----

  readonly stats = computed(() => {
    const r = this.release();
    const passed = r?.numberOfPassingTests ?? 0;
    const failed = r?.numberOfFailingTests ?? 0;
    const total = r?.numberOfTests ?? 0;
    const complete = passed + failed;
    const remaining = Math.max(0, total - complete);
    const pct = total > 0 ? Math.round((complete / total) * 100) : 0;
    const failPct = total > 0 ? Math.round((failed / total) * 100) : 0;
    return { passed, failed, total, complete, remaining, pct, failPct };
  });

  /** Release-wide showstopper banner: count + the owning tester (if any). */
  readonly showstopper = computed(() => {
    const count = this.release()?.numberOfShowstoppers ?? 0;
    if (count <= 0) return null;
    const owner = this.plans().find((p) => p.numberOfShowstoppers > 0)?.assignedUserName ?? null;
    return { count, owner };
  });

  // ---- per-plan presentation ----

  statusClass(p: TestPlan): string {
    switch (p.status) {
      case 'Completed':
        return 'done';
      case 'Failing':
        return 'failing';
      case 'InProgress':
        return 'partial';
      default:
        return 'untested';
    }
  }

  statusSub(p: TestPlan): string {
    const label =
      p.status === 'NotStarted'
        ? 'Not Started'
        : p.status === 'InProgress'
          ? 'In Progress'
          : p.status; // Failing | Completed
    if (p.numberOfFailingTests > 0) {
      const n = p.numberOfFailingTests;
      return `${label} · ${n} failure${n === 1 ? '' : 's'} flagged`;
    }
    return label;
  }

  cta(p: TestPlan): string {
    if (p.status === 'Completed') return 'Review';
    if (p.status === 'NotStarted') return 'Start';
    return 'Continue';
  }

  progressPct(p: TestPlan): number {
    return p.numberOfTests > 0 ? Math.round((p.numberOfPassingTests / p.numberOfTests) * 100) : 0;
  }

  failPct(p: TestPlan): number {
    return p.numberOfTests > 0 ? Math.round((p.numberOfFailingTests / p.numberOfTests) * 100) : 0;
  }

  // ---- actions ----

  /**
   * Open a plan straight into the Execute Test Case screen (PBI 55100) instead of
   * the older plan/test-case browser. We fetch the plan's test cases, resume at the
   * first one that hasn't been run yet (falling back to the first), and let the
   * stepper handle moving through the rest. The plan id rides along as a query param.
   */
  openPlan(p: TestPlan): void {
    const releaseId = this.release()?.releaseId;
    if (releaseId == null || this.isOpening(p)) return;

    this.viewState.setSelectedPlanId(releaseId, p.testPlanId);
    this.openingIds.update((s) => new Set(s).add(p.testPlanId));

    this.testCaseService
      .getByTestPlanId(p.testPlanId)
      .pipe(
        finalize(() =>
          this.openingIds.update((s) => {
            const next = new Set(s);
            next.delete(p.testPlanId);
            return next;
          }),
        ),
      )
      .subscribe({
        next: (cases) => {
          if (!cases.length) {
            this.toast.error('This plan has no test cases yet.');
            return;
          }
          const target = cases.find((c) => c.isPassing == null) ?? cases[0];
          this.router.navigate(['/releases', releaseId, 'test-case', target.testCaseId], {
            queryParams: { planId: p.testPlanId },
          });
        },
        error: () => this.toast.error('Could not open this plan. Please try again.'),
      });
  }

  claim(p: TestPlan): void {
    const userId = this.currentUserId();
    if (userId == null || this.isClaiming(p)) return;

    this.claimingIds.update((s) => new Set(s).add(p.testPlanId));
    this.releaseService
      .assignPlan(p.testPlanId, userId)
      .pipe(
        finalize(() =>
          this.claimingIds.update((s) => {
            const next = new Set(s);
            next.delete(p.testPlanId);
            return next;
          }),
        ),
      )
      .subscribe({
        next: (updated) => {
          this.overrides.update((o) => ({
            ...o,
            [p.testPlanId]: {
              assignedUserId: updated.assignedUserId ?? userId,
              assignedUserName: updated.assignedUserName ?? null,
            },
          }));
          this.toast.success('Plan assigned to you');
        },
        error: () => this.toast.error('Failed to claim the plan.'),
      });
  }

  /** Export the plans this view actually renders — both sections, filters applied (AC2, AC4). */
  exportCsv(): void {
    const visible = [...this.assignedToMe(), ...this.unassigned()];
    const rows = [
      ['Plan name', 'Status', 'Progress (X/Y)', 'Assignee', 'Failures'],
      ...visible.map((p) => [
        p.testPlanName,
        p.status,
        `${p.numberOfPassingTests}/${p.numberOfTests}`,
        p.assignedUserName ?? 'Unassigned',
        String(p.numberOfFailingTests),
      ]),
    ];
    downloadCsv(rows, this.release()?.releaseName, 'plans');
  }

  trackByPlanId = (_: number, p: TestPlan) => p.testPlanId;

  private applyFilters(plans: TestPlan[]): TestPlan[] {
    const term = this.search().trim().toLowerCase();
    const filter = this.statusFilter();
    return plans
      .filter((p) => matchesStatus(p.status, filter))
      .filter((p) => !term || p.testPlanName.toLowerCase().includes(term));
  }
}

function matchesStatus(status: TestPlanStatus, filter: StatusFilter): boolean {
  if (filter === 'All') return true;
  if (filter === 'InProgress') return status === 'InProgress' || status === 'Failing';
  if (filter === 'NotStarted') return status === 'NotStarted';
  return status === 'Completed';
}
